/*
 * Custom sort dropdown for the 2026 collection grid.
 *
 * The native <select> (#SortBydesktop) is kept in the DOM but hidden via CSS so
 * the theme's existing AJAX sort logic (component-filtering.js) keeps working.
 * This component renders an on-brand button + listbox and, on selection, sets the
 * native select's value and dispatches a `change` event to trigger the AJAX sort.
 */
(function () {
  if (customElements.get('cl26-sort-dropdown')) return;

  class Cl26SortDropdown extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('.cl26-sort__button');
      this.list = this.querySelector('.cl26-sort__list');
      this.currentEl = this.querySelector('[data-cl26-sort-current]');
      this.options = Array.from(this.querySelectorAll('.cl26-sort__option'));
      this.selectId = this.dataset.selectId;
      this.activeIndex = this.options.findIndex(function (o) {
        return o.getAttribute('aria-selected') === 'true';
      });

      if (!this.button || !this.list) return;

      this.onDocClick = this.handleDocClick.bind(this);

      this.button.addEventListener('click', this.toggle.bind(this));
      this.options.forEach((opt) => {
        opt.addEventListener('click', () => this.select(opt));
      });
      document.addEventListener('click', this.onDocClick);
      this.addEventListener('keydown', this.handleKeyDown.bind(this));
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocClick);
    }

    get isOpen() {
      return !this.list.hidden;
    }

    toggle(event) {
      if (event) event.stopPropagation();
      if (this.isOpen) {
        this.close();
      } else {
        this.open();
      }
    }

    open() {
      this.list.hidden = false;
      this.button.setAttribute('aria-expanded', 'true');
      const target = this.options[this.activeIndex] || this.options[0];
      if (target) target.focus();
    }

    close() {
      this.list.hidden = true;
      this.button.setAttribute('aria-expanded', 'false');
    }

    handleDocClick(event) {
      if (!this.contains(event.target)) this.close();
    }

    select(option) {
      const value = option.dataset.value;

      this.options.forEach((o) => {
        o.setAttribute('aria-selected', o === option ? 'true' : 'false');
      });
      this.activeIndex = this.options.indexOf(option);
      if (this.currentEl) this.currentEl.textContent = option.textContent.trim();

      const select = document.getElementById(this.selectId);
      if (select && select.value !== value) {
        select.value = value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }

      this.close();
      this.button.focus();
    }

    move(direction) {
      if (!this.options.length) return;
      const count = this.options.length;
      const start = this.activeIndex < 0 ? 0 : this.activeIndex;
      this.activeIndex = (start + direction + count) % count;
      this.options[this.activeIndex].focus();
    }

    handleKeyDown(event) {
      switch (event.key) {
        case 'Escape':
          if (this.isOpen) {
            this.close();
            this.button.focus();
          }
          break;
        case 'ArrowDown':
          event.preventDefault();
          if (!this.isOpen) {
            this.open();
          } else {
            this.move(1);
          }
          break;
        case 'ArrowUp':
          event.preventDefault();
          if (!this.isOpen) {
            this.open();
          } else {
            this.move(-1);
          }
          break;
        case 'Home':
          if (this.isOpen) {
            event.preventDefault();
            this.activeIndex = 0;
            this.options[0].focus();
          }
          break;
        case 'End':
          if (this.isOpen) {
            event.preventDefault();
            this.activeIndex = this.options.length - 1;
            this.options[this.activeIndex].focus();
          }
          break;
        case 'Enter':
        case ' ': {
          const active = document.activeElement;
          if (this.isOpen && active && active.classList.contains('cl26-sort__option')) {
            event.preventDefault();
            this.select(active);
          }
          break;
        }
        default:
          break;
      }
    }
  }

  customElements.define('cl26-sort-dropdown', Cl26SortDropdown);
})();
